import { Component, ChangeDetectorRef, Input } from '@angular/core';
import { Frame, TableConfig, GenericFilterComponent, GenericFilter, template, ModalBase, modal } from 'curvy';
import { Task } from '@task-utils/types';
import { TaskRoutes } from '@task-utils/routes';

function status_color(status: number) {
    if (status < 200 || status >= 600) return "#aaa";
    if (status < 300) return "#9fa";
    if (status < 400) return "#9af";
    if (status < 500) return "#faa";
    return "#f55; color: white;";
}

function method_color(method: string) {
    if (method=="GET") return "#fa9";
    if (method=="POST") return "#9af";
    if (method=="PATCH") return "#9f9";
    if (method=="DELETE") return "#f99";
    return "#aaa";
}

function opt(lbl: string) {
    return {
        label: lbl,
        value: lbl
    };
}

type Route = { name: string; val: string};

let all_routes: Route[] = [];
function get_all_routes() {
    let args = new Array(8).fill("%");
    if (all_routes.length != 0) return all_routes;

    for (let key of Object.keys(TaskRoutes)) {
        let ns = TaskRoutes[key];
        if (typeof(ns) != "object") continue;

        for (let name in ns) {
            if (name.startsWith("api")) continue;
            if (typeof(ns[name]) == "string") {
                let val = ns[name].replace(TaskRoutes.backend, "");
                all_routes.push({ name, val });

            } else if (typeof(ns[name]) == "function") {
                let val = ns[name](...args).replace(TaskRoutes.backend, "");
                all_routes.push({name, val })
            }
        }
    }

    return all_routes;
}

@Component({
    selector: 'task-debug-requests-json',
    template: `
        @if (type=="string" || type=="number" || type=="boolean") {
            {{value}}
        } @else if (value == null) {
            null
        } @else if (is_array) {
            <ol>
            @for (item of value ; track item) {
                <li>
                    <task-debug-requests-json [value]="item">
                    </task-debug-requests-json>
                </li>
            }
            </ol>
        } @else if (type=="object") {
            <ul>
            @for (key of keys ; track key) {
                <li>
                    <b>{{key}}</b>:
                    <task-debug-requests-json [value]="value[key]">
                    </task-debug-requests-json>
                </li>
            }
            </ul>
        }
    `,
    styles: [`
        :host {
            ul, ol {
                padding: 0;
                margin: 0;
                padding-left: 10px;
            }
        }
    `]
})
export class DebugRequestsJSON {
    @Input()
    value: any;
    type = "";
    is_array = false;
    keys = [""];

    ngOnInit() {
        this.type = typeof this.value;
        this.is_array = Array.isArray(this.value);
        if (this.value) {
            this.keys = Object.keys(this.value);
        }
    }
}

@Component({
    selector: 'task-debug-requests-dialog',
    template: `
        <div style="max-height: 75vh; overflow: auto">
            <task-debug-requests-json [value]="injectedData">
            </task-debug-requests-json>
        </div>
    `,
    styles: []
})
export class DebugRequestsDialog extends ModalBase<any, any> {
}

@Component({
    selector: 'task-debug-requests',
    template: `
        <knw-data-table [config]="tableConfig"></knw-data-table>
    `,
    styles: [`
        :host {
            display: flex;
            flex-flow: column nowrap;
            overflow: hidden !important;
            outline: 2px red;

            .req-status {
                padding: 0 4px;
                border-radius: 2px;
                box-shadow: inset 0 1px #fff8, inset 0 -1px #0004;
            }

            .req-method {
                padding: 0 2px;
                margin-right: 2px;
                width: 7ch;
                text-align: center;
                border-radius: 2px;
                box-shadow: inset 0 1px #fff8, inset 0 -1px #0004;
            }
        }
    `]
})
export class RequestsComponent {
    constructor(public cdr: ChangeDetectorRef) {
        Frame.set({
            title: "NAVROUTE.REQUESTS",
            visible: true,
            layout: "middle",
            size: "full"
        });
    }

    tableConfig: TableConfig<Task.Debug_Request, DisplayRequest, 'debug-requests', GenericFilterComponent> = {
        tableName: 'debug-requests',
        defaultPageSize: 200,

        getData: async (filter, paginator) => {
            let parsed_filter = {
                search: [] as string[],
                attributes: [] as string[],
                sort: {
                    label: "#:debug_request_id",
                    direction: "desc" as const
                }
            };

            for (let key in filter) {
                switch (key) {
                    case "#=:company_id": {
                        parsed_filter.attributes.push(key);
                        parsed_filter.search.push(filter[key].company_id);
                    } break;

                    case "S:status": {
                        parsed_filter.attributes.push(key);
                        parsed_filter.search.push({
                            OK: '2%',
                            CLIENT_ERR: '4%',
                            SERVER_ERR: '5%',
                        }[filter[key]] ?? '%')
                    } break;

                    case "url": {
                        parsed_filter.attributes.push(key);
                        parsed_filter.search.push(filter[key].val);
                    } break;

                    default:
                        parsed_filter.attributes.push(key);
                        parsed_filter.search.push(filter[key])
                }
            }

            return await TaskRoutes.debug.api_debug_request_search(parsed_filter, paginator);
        },
        getHeader: (header) => {
            switch (header) {
                case 'status':
                    return { icon: "health_and_safety", label: "STATUS", sortable: false }

                case 'request':
                    return { icon: "http", label: "REQUEST", sortable: false };

                case 'user':
                    return { icon: "person", label: "USER", sortable: false };

                case 'time':
                    return { icon: "schedule", label: "TIME", sortable: false };
            }
        },
        rowActions: [
            {
                icon: 'contact_support',
                label: 'BODY',
                onClick: (r) => {
                    modal.open(DebugRequestsDialog, null, r.body);
                },
                isVisible: (r) => !!r.body,
                priority: 1
            },
            {
                icon: 'feedback',
                label: 'RESPONSE',
                onClick: (r) => {
                    modal.open(DebugRequestsDialog, null, r.response);
                },
                isVisible: (r) => !!r.response,
                priority: 2
            },
            {
                icon: 'article',
                label: 'LOGS',
                onClick: (r) => {
                    modal.open(DebugRequestsDialog, null, r.log);
                },
                isVisible: (r) => !!r.log.length,
                priority: 3
            },
        ],
        showHeaderWhenEmpty: true,
        unwrapData: (req) => {
            req.url = req.url.replace(TaskRoutes.backend, "");
            let duration = 0;
            duration = (
                new Date(req.end_time).getTime() -
                new Date(req.start_time).getTime()
            );
            if (duration < 0) duration = 0;

            return {
                status: template.transform(`
                    <div class="req-status" style="background: ${status_color(req.status)}">
                        {{ status }}
                    </div>
                `, req),

                request: template.transform(`
                    <div class="flex-row flex-dynamic" style="text-align: left">
                        <div class="req-method" style="background: ${method_color(req.method)}">{{method}}</div>
                        <b>{{url}}</b>
                    </div>
                    `, req
                ),

                user: template.transform(`
                    <div class="flex-column flex-dynamic" style="text-align: left">
                        <span>${req.company_name} (#${req.company_id ?? "-"})</span>
                        <span>${req.user_name ?? ''} (#${req.user_id ?? "-"})</span>
                    </div>
                `, req),

                time: template.transform(`
                    <div class="flex-column flex-dynamic">
                        <span>{{ start_time | date }}</span>
                        <span>${duration || '?'}ms</span>
                    </div>
                `, req),
            };
        },

        filterComponent: GenericFilterComponent,
        filterInput: async () => {
            let filters: GenericFilterComponent["filterInput"] = [
                GenericFilter.text("SEARCH", "body+ +response+ +user_name+ +company_name"),
                GenericFilter.dropdown("METHOD", "=:method", [opt("GET"), opt("POST"), opt("PATCH"), opt(" DELETE")]),
                GenericFilter.dropdown("STATUS", "S:status", [opt("OK"), opt("CLIENT_ERR"), opt("SERVER_ERR")]),
                GenericFilter.search("URL", "url", this.search_urls, this.format_url),
                GenericFilter.search("COMPANY", "#=:company_id", this.search_companies, this.format_company_name),
                GenericFilter.date_range("From", "To", "daterange")
            ];
            return filters;
        },
    }

    async search_urls(search: string) {
        let parts = search.split(" ");

        return get_all_routes().filter(r => {
            for (let p of parts) {
                if (!r.name.includes(p) && !r.val.includes(p)) return false;
            }
            return true;
        });
    }

    format_url(route: Route) {
        return `${route.name} (${route.val})`;
    }

    async search_companies(search: string) {
        let res = await TaskRoutes.companies.api_company_search(
            { search: [Task.COMPANY_TYPE.MAIN, search], attributes: ["#=:company_type_id", "name"] },
            { page_no: 1, page_size: 10 }
        );

        return res.data;
    }

    format_company_name(comp: Task.Company) {
        if (!comp) return "";
        return comp.name;
    }

    async search_users(search: string) {
        let res = await TaskRoutes.users.api_user_search(
            { search: [search], attributes: ["first_name+ +last_name+ +email"] },
            { page_no: 1, page_size: 10 }
        );

        return res.data;
    }

    format_user(user: Task.User) {
        if (!user) return "";
        return `${user.first_name} ${user.last_name} (${user.email})`;
    }

}

type DisplayRequest = {
    status: template.EscapedHTML;
    request: template.EscapedHTML;
    user: template.EscapedHTML;
    time: template.EscapedHTML;
}
